import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Button } from '@/components/shadcn/button'
import { Calendar } from '@/components/shadcn/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { format, parse, isValid } from 'date-fns'
import { cn } from '@/lib/utils'
import { BaseFieldProps } from './types'
import { IconCalendarOutline } from '@icons/IconCalendarOutline'
import clsx from 'clsx'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconInformationOutline } from '@icons/IconInformationOutline'
import { DocumentationButton } from './DocumentationButton'
import { getDatePickerCalendarProps } from '@/lib/datePickerCalendarProps'

export const DateField: React.FC<BaseFieldProps> = ({ field, formFieldName, control, variant }) => {
  return (
    <FormField
      control={control}
      name={formFieldName}
      render={({ field: formField }) => {
        const toDate = (value: unknown): Date | undefined => {
          if (!value) return undefined
          if (value instanceof Date) return isValid(value) ? value : undefined
          if (typeof value === 'string') {
            const input = value.trim()
            // Try native Date first for ISO strings
            const native = new Date(input)
            if (!Number.isNaN(native.getTime())) return native
            // Try known patterns
            const patterns = ['yyyy-MM-dd', 'dd-MM-yyyy', 'yyyy/MM/dd', 'dd/MM/yyyy']
            for (const p of patterns) {
              const d = parse(input, p, new Date())
              if (isValid(d)) return d
            }
            // Try with normalized separators
            const normalized = input.replace(/\//g, '-')
            for (const p of ['yyyy-MM-dd', 'dd-MM-yyyy']) {
              const d = parse(normalized, p, new Date())
              if (isValid(d)) return d
            }
          }
          return undefined
        }
        const selectedDate = toDate(formField.value)
        const isCompact = variant === 'row' || variant === 'inline'
        const fieldControl = (
          <Popover>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant="input"
                  className={cn(
                    'w-full pl-3 text-left font-normal',
                    !selectedDate && 'text-muted-foreground',
                    isCompact && 'h-6',
                    variant === 'inline' && 'border-none'
                  )}
                  disabled={field.readOnly}
                >
                  {selectedDate ? format(selectedDate, 'PP') : <span>Pick a date</span>}
                  <IconCalendarOutline className="ml-auto size-5" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  if (date) {
                    const formattedDate = format(date, 'yyyy-MM-dd')
                    formField.onChange(formattedDate)
                  }
                }}
                initialFocus
                {...getDatePickerCalendarProps(selectedDate)}
              />
            </PopoverContent>
          </Popover>
        )
        return (
          <FormItem
            className={clsx('flex flex-1 flex-col', field.widget === 'hidden' && 'hidden', {
              'space-y-0': variant === 'inline',
            })}
          >
            <div className={clsx('flex justify-start gap-2', { hidden: variant === 'inline' })}>
              <FormLabel className={variant === 'row' ? 'sr-only' : ''}>{field.title}</FormLabel>
              {!isCompact && <DocumentationButton documentation={field.documentation} />}
            </div>
            <FormDescription
              className={clsx({
                'sr-only': variant === 'row',
                hidden: variant === 'inline',
                'text-xs': variant === 'column' || !variant,
              })}
            >
              {field.description}
            </FormDescription>
            {variant === 'row' ? (
              <div className="!mt-0 flex items-center gap-x-1 text-muted-foreground">
                <TooltipButton tooltip={field.title}>
                  <div className="shrink-0">
                    <IconInformationOutline className="size-4" />
                  </div>
                </TooltipButton>
                {fieldControl}
              </div>
            ) : (
              fieldControl
            )}
            {!isCompact && <FormMessage />}
          </FormItem>
        )
      }}
    />
  )
}
